/**
 * «Рассылки» → «Страница брони»: из выданных клиентам ссылок `my.uni-renter.ru/d/<хэш>` —
 * сколько зашли, перешли в мессенджеры, запустили ботов MAX и Telegram, подписались
 * на push и почту. Строка — день, неделя или месяц ВЫДАЧИ ссылки (когорта: что
 * сработало из выданного). Ниже — каждая ссылка периода. Спека
 * `docs/specs/2026-10-02-deal-link-stats-design.md`; числа — как у `prokat-runner deal-links`.
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { iso, plusDays } from "@/lib/dates";
import { type LinkCounts, type LinkGroup, type LinkItem, loadDealLinks } from "@/lib/dealLinks";

const GROUP: Record<LinkGroup, { label: string; head: string; hint: string }> = {
  day: { label: "по дням", head: "День", hint: "День — дата выдачи ссылки" },
  week: { label: "по неделям", head: "Неделя", hint: "Неделя — с понедельника, крайние обрезаны периодом" },
  month: { label: "по месяцам", head: "Месяц", hint: "Месяц — с 1-го числа, крайние обрезаны периодом" },
};
const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

type Col = { key: keyof LinkCounts; head: string; hint: string; of?: keyof LinkCounts; tone?: string; sep?: boolean };
/** Колонки блоками: ссылки → переходы → подписки. Доли переходов и подписок — от зашедших. */
const BLOCKS: { label: string; cols: Col[] }[] = [
  {
    label: "Ссылки",
    cols: [
      { key: "issued", head: "Выдано", hint: "ссылок выдано в период: одна на сделку и повод" },
      { key: "opened", head: "Зашли", hint: "открыли страницу хотя бы раз", of: "issued", tone: "text-ok" },
      { key: "opens", head: "Открытий", hint: "всего открытий страницы", sep: true },
    ],
  },
  {
    label: "Перешли в мессенджер",
    cols: [
      { key: "click_max", head: "MAX", hint: "нажали MAX на странице", of: "opened" },
      { key: "click_telegram", head: "Telegram", hint: "нажали Telegram на странице", of: "opened" },
      { key: "click_whatsapp", head: "WhatsApp", hint: "нажали WhatsApp на странице", of: "opened", sep: true },
    ],
  },
  {
    label: "Подписались на уведомления",
    cols: [
      { key: "bot_max", head: "Бот MAX", hint: "запустили бота MAX с хэшем брони", of: "opened", tone: "text-ok" },
      { key: "bot_telegram", head: "Бот Telegram", hint: "запустили @unirenterbot с хэшем брони", of: "opened", tone: "text-ok" },
      { key: "push", head: "Push", hint: "живая push-подписка браузера (отписавшиеся не считаются)", of: "opened", tone: "text-ok" },
      { key: "push_denied", head: "Отказ push", hint: "браузер отказал в push хотя бы раз", of: "opened", tone: "text-err" },
      { key: "email", head: "E-mail", hint: "оставили адрес почты; писем пока не шлём", of: "opened", tone: "text-ok" },
      { key: "any", head: "Хоть одна", hint: "бот MAX, бот Telegram, push или почта", of: "opened", tone: "text-ok" },
    ],
  },
];
const COLS = BLOCKS.flatMap((b) => b.cols);

function pct(a: number, b: number): string {
  return b ? `${Math.round((a * 100) / b)}%` : "";
}

function dm(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** «02.10 06:47» из `2026-10-02 06:47`. */
function dmt(at: string | null): string {
  return at ? `${dm(at)} ${at.slice(11, 16)}` : "—";
}

function title(d: { day: string; to: string }, group: LinkGroup): string {
  if (group === "month") {
    const x = new Date(`${d.day}T12:00:00`);
    const name = `${x.toLocaleDateString("ru-RU", { month: "long" })} ${d.day.slice(0, 4)}`;
    const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
    return d.day.slice(8) === "01" && Number(d.to.slice(8)) === last ? name : `${name} · ${dm(d.day)} – ${dm(d.to)}`;
  }
  return d.day === d.to
    ? `${dm(d.day)} ${WEEKDAY[new Date(`${d.day}T12:00:00`).getDay()]}`
    : `${dm(d.day)} – ${dm(d.to)}`;
}

function Cells({ c }: { c: LinkCounts }) {
  return (
    <>
      {COLS.map((col) => {
        const v = c[col.key];
        return (
          <td
            key={col.key}
            className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${col.sep ? "border-r border-border" : ""}`}
          >
            <span className={`text-[14px] font-semibold ${v ? (col.tone ?? "text-fg") : "text-fg-faint"}`}>{v}</span>
            {col.of && v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, c[col.of])}</span>}
          </td>
        );
      })}
    </>
  );
}

function Num({ v, tone = "text-fg" }: { v: number; tone?: string }) {
  return <span className={`tabular-nums ${v ? tone : "text-fg-faint"}`}>{v}</span>;
}

function LinkRow({ x }: { x: LinkItem }) {
  const td = "whitespace-nowrap px-3 py-1.5";
  return (
    <tr className="border-t border-border hover:bg-bg-hover" data-annot={`ссылка сделки ${x.deal_id}`}>
      <td className={`${td} pl-4`}>
        <Link to={`/deals/${x.deal_id}`} className="font-medium text-accent hover:underline" title="карточка сделки">
          {x.deal_id}
        </Link>
        <a
          href={x.url}
          target="_blank"
          rel="noreferrer"
          className="ml-2 inline-flex align-middle text-fg-faint hover:text-fg"
          title="открыть страницу брони, как её видит клиент (засчитается как открытие)"
        >
          <ExternalLink size={12} />
        </a>
      </td>
      <td className={`${td} text-fg-muted`}>{x.source === "manual" ? "руками" : x.source}</td>
      <td className={`${td} tabular-nums`}>{dmt(x.issued)}</td>
      <td className={`${td} border-r border-border`}>
        <Num v={x.opens} />
        {x.opens > 0 && (
          <span className="ml-1.5 text-[11.5px] text-fg-muted">
            {dmt(x.open_first)}
            {x.open_last !== x.open_first ? ` – ${dmt(x.open_last)}` : ""}
          </span>
        )}
      </td>
      <td className={td}><Num v={x.clicks.max} /></td>
      <td className={td}><Num v={x.clicks.telegram} /></td>
      <td className={`${td} border-r border-border`}><Num v={x.clicks.whatsapp} /></td>
      <td className={td}><Num v={x.bot_max} tone="text-ok" /></td>
      <td className={td}><Num v={x.bot_telegram} tone="text-ok" /></td>
      <td className={td}><Num v={x.push} tone="text-ok" /></td>
      <td className={td}><Num v={x.push_denied} tone="text-err" /></td>
      <td className={td}><Num v={x.email} tone="text-ok" /></td>
    </tr>
  );
}

export function LinksTab() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const group: LinkGroup =
    params.get("group") === "week" || params.get("group") === "month" ? (params.get("group") as LinkGroup) : "day";
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["deal-links", from, to, group], queryFn: () => loadDealLinks(from, to, group) });
  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const r = q.data;

  return (
    <section className="flex flex-col gap-3" data-annot="страница брони">
      <div className="flex flex-wrap items-center gap-3" data-annot="период страницы брони">
        <DateRangePicker label="Выдана" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
        <div className="flex gap-1" role="group" aria-label="Группировка">
          {(Object.keys(GROUP) as LinkGroup[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set({ group: k === "day" ? "" : k })}
              aria-pressed={group === k}
              className={seg(group === k)}
            >
              {GROUP[k].label}
            </button>
          ))}
        </div>
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю ссылки…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(q.error.message)}</div>}
      {r && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`страница брони ${GROUP[group].label}`}>
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`} rowSpan={2}>
                    {GROUP[group].head}
                  </th>
                  {BLOCKS.map((b) => (
                    <th key={b.label} className={`${th} border-r border-border font-normal`} colSpan={b.cols.length}>
                      {b.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {COLS.map((c) => (
                    <th key={c.key} className={`${th} ${c.sep ? "border-r border-border" : ""}`} title={c.hint}>
                      {c.head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.rows.map((d) => {
                  const wd = new Date(`${d.day}T12:00:00`).getDay();
                  return (
                    <tr
                      key={d.day}
                      className={`border-t border-border hover:bg-bg-hover ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`}
                      data-annot={`${GROUP[group].head.toLowerCase()} ${d.day}`}
                    >
                      <td className={`whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums ${d.issued ? "" : "text-fg-faint"}`}>
                        {title(d, group)}
                      </td>
                      <Cells c={d} />
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-border font-medium" data-annot="итого за период">
                  <td className="py-2 pl-4 pr-3 text-fg-muted">Итого {title({ day: r.from, to: r.to }, "week")}</td>
                  <Cells c={r.total} />
                </tr>
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            {GROUP[group].hint}. Ссылка — одна на сделку и повод; в каждом столбце считается один раз. Доля «Зашли» — от
            выданных, переходы и подписки — от зашедших. Подписки — как сейчас: отписался — не считается.
            {Object.keys(r.sources).length > 1 && (
              <>
                {" "}
                Поводы:{" "}
                {Object.entries(r.sources)
                  .map(([k, v]) => `${k === "manual" ? "руками" : k} ${v}`)
                  .join(", ")}
                .
              </>
            )}
          </div>
        </section>
      )}
      {r && r.links.length > 0 && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="ссылки периода">
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`}>Сделка</th>
                  <th className={th}>Повод</th>
                  <th className={th} title="когда ссылка выдана, MSK">Выдана</th>
                  <th className={`${th} border-r border-border`} title="открытий страницы: первое – последнее">Открытий</th>
                  <th className={th} title="переходов в MAX со страницы">MAX</th>
                  <th className={th} title="переходов в Telegram со страницы">Telegram</th>
                  <th className={`${th} border-r border-border`} title="переходов в WhatsApp со страницы">WhatsApp</th>
                  <th className={th} title="людей, запустивших бота MAX с хэшем брони">Бот MAX</th>
                  <th className={th} title="чатов, запустивших @unirenterbot с хэшем брони">Бот Telegram</th>
                  <th className={th} title="живых push-подписок (устройств)">Push</th>
                  <th className={th} title="отказов браузера в push">Отказ</th>
                  <th className={th} title="живых адресов почты">E-mail</th>
                </tr>
              </thead>
              <tbody>
                {r.links.map((x) => (
                  <LinkRow key={x.id} x={x} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            Ссылки периода, свежие сверху. Кто именно запустил бота и какие адреса — команда «deal-link ID» в консоли.
          </div>
        </section>
      )}
    </section>
  );
}
